import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'

// Registered once here so every consumer of `formatCardExpiryDate` (and any
// other `dayjs().format('LL'/'ll'/etc.)` call in the app) gets long localized
// date formats, without each caller needing to remember to extend dayjs
// itself.
dayjs.extend(localizedFormat)

/**
 * Formats a card expiry date (ISO string, e.g. from GraphQL `currentCard.expiryDate`)
 * into a long localized date such as "July 14, 2026". Used by the wallet card,
 * wallet page, and card-expiry banner so the format stays consistent and only
 * needs to change in one place.
 */
export const formatCardExpiryDate = (date?: string | null): string | null =>
  date ? dayjs(date).format('LL') : null

const calculateAge = (birthday: Date) => {
  if (isNaN(birthday.getTime())) {
    return -1
  }
  // @ts-ignore
  const ageDifMs = Date.now() - birthday
  const ageDate = new Date(ageDifMs) // miliseconds from epoch
  return Math.abs(ageDate.getUTCFullYear() - 1970)
}

type Maybe<T> = T | null | undefined
type StringOrNumber = string | number

type StringDate = {
  day: Maybe<StringOrNumber>
  month: Maybe<StringOrNumber>
  year: Maybe<StringOrNumber>
}

type DateParts = {
  day: number
  month: number
  year: number
}

export class StringDateParseError extends Error {}

const validateStringDate = ({day, month, year}: StringDate): boolean => {
  // Check if provided strings can be parsed into date.
  if (isNaN(Date.parse(`${year}-${month}-${day}`))) {
    return false
  }

  // Check if day is incremented into next month
  const date = new Date(`${year}-${month}-${day}`)
  const isValidDay =
    Boolean(+date) && date.getDate() === parseInt(day as string, 10)
  // Check if month is the correct month and doesn't overflow the year
  const isValidMonth =
    Boolean(+date) &&
    date.getMonth() + 1 === parseInt(month as string, 10) &&
    date.getFullYear() === parseInt(year as string, 10)

  return isValidDay && isValidMonth
}

/**
 * Format day, month, year into ISO string.
 * If it's not valid as a date, it throws StringDateParseError
 * @param StringDate
 */
const isoFormat = ({day, month, year}: StringDate): string => {
  const monthString = (month as number) < 10 ? `0${month}` : `${month}`
  const dayString = (day as number) < 10 ? `0${day}` : `${day}`
  return `${year}-${monthString}-${dayString}`
}

const isValidDate = ({day, month, year}: DateParts): boolean => {
  const date = new Date(year, month - 1, day)
  return (
    date.getDate() === day &&
    date.getMonth() + 1 === month &&
    date.getFullYear() === year
  )
}
export const formatDateToFormattedString = (date?: Date | null) => {
  if (!date) return dayjs().format('YYYY-MM-DD')
  return dayjs(date).format('YYYY-MM-DD')
}

/**
 * Return the year, month and day as numbers in an object
 *
 * Do NOT try to convert to date, as JavaScript does some "convenient"
 * converting of values to a valid date (31 June = 1 July).
 *
 * @param birthDate IsoString that is returned from BE as Date
 * @see isValidDate
 */
const parseMemberBirthDay = (birthDate: Maybe<string>) => {
  if (!!birthDate) {
    const [year, month, day] = birthDate
      .split('-', 3)
      .map((p) => parseInt(p, 10))
    return {
      year,
      month,
      day,
    }
  }

  return {
    day: '',
    month: '',
    year: '',
  }
}

const MonthsEn = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
const MonthsDe = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
]

const isoStringToi18nDisplayString = (dateStr: string, lang: string) => {
  const date = new Date(dateStr)
  const timeStr = date.toLocaleTimeString('en', {
    hour: 'numeric',
    minute: '2-digit',
  })

  switch (lang) {
    case 'en':
      return `${date.getDate()} of ${MonthsEn[date.getMonth()]}, ${timeStr}`
    case 'de':
      return `${date.getDate()} von ${MonthsDe[date.getMonth()]}, ${timeStr}`
  }
}

export {
  calculateAge,
  validateStringDate,
  isoFormat,
  isValidDate,
  parseMemberBirthDay,
  isoStringToi18nDisplayString,
}
